// src/pages/AdminDepartmentsPage.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { departmentIconMap, departmentIconList } from "../utils/departmentIcons";
import { useAuth } from "../context/AuthContext";

function AdminDepartmentsPage() {
  const { authFetch, user } = useAuth();

  const role = String(user?.role || "").toLowerCase();
  const isSuperadmin = role === "superadmin";

  const canManageDepartments = isSuperadmin || role === "manager";
  const canManage = canManageDepartments;

  const [departments, setDepartments] = useState([]);
  const [staff, setStaff] = useState([]);

  const [deptSearch, setDeptSearch] = useState("");
  const [managerFilter, setManagerFilter] = useState("all");
  const [deptSort, setDeptSort] = useState("az");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Sub-duties modal toggle
  const [showSubModal, setShowSubModal] = useState(false);

  // ✅ All Departments (Hierarchy editor) modal
  const [showDeptModal, setShowDeptModal] = useState(false);

  // ✅ Confirm + Success UI modals (center)
  const [confirmSave, setConfirmSave] = useState({
    open: false,
    deptId: null,
    deptName: "",
  });
  const [saveSuccess, setSaveSuccess] = useState({
    open: false,
    message: "",
    isError: false,
  });

  // ✅ NEW: Delete confirm modal
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    deptId: null,
    deptName: "",
  });
  const [deletingDeptId, setDeletingDeptId] = useState(null);

  // ---- Add Dept Modal ----
  const [showAddDept, setShowAddDept] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newColor, setNewColor] = useState("#2563eb");
  const [newIcon, setNewIcon] = useState("FcBriefcase");
  const [showIconPicker, setShowIconPicker] = useState(false);

  // ✅ NEW: Edit Dept (color + icon) for hierarchy modal
  const [editColor, setEditColor] = useState("#2563eb");
  const [editIcon, setEditIcon] = useState("FcBriefcase");
  const [showEditIconPicker, setShowEditIconPicker] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [movingUserId, setMovingUserId] = useState(null);

  // ---- Sub-duties (for modal) ----
  const [selectedDeptId, setSelectedDeptId] = useState("");
  const [subduties, setSubduties] = useState([]);
  const [newSubName, setNewSubName] = useState("");
  const [errorSub, setErrorSub] = useState("");

  // ✅ NEW: sub-duty edit/delete states
  const [editingSubId, setEditingSubId] = useState(null);
  const [editingSubName, setEditingSubName] = useState("");
  const [savingSubId, setSavingSubId] = useState(null);
  const [deletingSubDutyId, setDeletingSubDutyId] = useState(null);

  // ---- hierarchy edits ----
  const [hierarchyEdits, setHierarchyEdits] = useState({});
  const [savingDeptId, setSavingDeptId] = useState(null);

  // selected dept object
  const selectedDept =
    departments.find((d) => String(d.id) === String(selectedDeptId)) || null;

  const selectedDeptName = selectedDept?.name || "Department";
  const selectedKey = selectedDept ? String(selectedDept.id) : "";

  // ✅ staff select options
  const staffOptions = useMemo(() => {
    const list = Array.isArray(staff) ? staff : [];
    return [
      { id: "", name: "(none)" },
      ...list.map((s) => ({ id: String(s.id), name: s.name })),
    ];
  }, [staff]);

  const managerOptions = useMemo(() => {
    const ids = new Set();

    for (const d of departments || []) {
      if (d.manager_user_id) ids.add(String(d.manager_user_id));
    }

    return Array.from(ids)
      .map((id) => ({
        id,
        name: staff.find((s) => String(s.id) === String(id))?.name || `User ${id}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [departments, staff]);

  // ✅ map: userId -> name
  const staffNameById = useMemo(() => {
    const m = {};
    for (const u of staff || []) m[String(u.id)] = u.name;
    return m;
  }, [staff]);

  // ✅ group staff by department_id
  const staffByDept = useMemo(() => {
    const map = {};
    for (const u of staff || []) {
      const did = u.department_id ? String(u.department_id) : "";
      if (!did) continue;
      if (!map[did]) map[did] = [];
      map[did].push(u);
    }
    return map;
  }, [staff]);

  const filteredDepartments = useMemo(() => {
    const q = String(deptSearch || "").trim().toLowerCase();

    let list = (departments || []).filter((dep) => {
      const deptName = String(dep.name || "").toLowerCase();
      const deptDesc = String(dep.description || "").toLowerCase();
      const managerId = dep.manager_user_id ? String(dep.manager_user_id) : "";

      const matchesSearch = !q || deptName.includes(q) || deptDesc.includes(q);

      const matchesManager =
        managerFilter === "all" || managerId === String(managerFilter);

      return matchesSearch && matchesManager;
    });

    list = [...list].sort((a, b) => {
      if (deptSort === "most") {
        const aCount = (staffByDept[String(a.id)] || []).length;
        const bCount = (staffByDept[String(b.id)] || []).length;
        return bCount - aCount;
      }

      if (deptSort === "least") {
        const aCount = (staffByDept[String(a.id)] || []).length;
        const bCount = (staffByDept[String(b.id)] || []).length;
        return aCount - bCount;
      }

      return String(a.name || "").localeCompare(String(b.name || ""));
    });

    return list;
  }, [departments, deptSearch, managerFilter, deptSort, staffByDept]);

  function nameOrNone(userId) {
    if (!userId) return "(none)";
    return staffNameById[String(userId)] || "(none)";
  }

  function initials(name) {
    const n = String(name || "").trim();
    if (!n) return "?";
    const parts = n.split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase()).join("");
  }

  // ✅ selected edit values (for modal editor)
  const selectedEdit = useMemo(() => {
    if (!selectedDept)
      return { manager_user_id: "", supervisor_user_id: "", lead_user_id: "" };
    const key = String(selectedDept.id);
    return (
      hierarchyEdits[key] || {
        manager_user_id: selectedDept.manager_user_id
          ? String(selectedDept.manager_user_id)
          : "",
        supervisor_user_id: selectedDept.supervisor_user_id
          ? String(selectedDept.supervisor_user_id)
          : "",
        lead_user_id: selectedDept.lead_user_id
          ? String(selectedDept.lead_user_id)
          : "",
      }
    );
  }, [hierarchyEdits, selectedDept]);

  // ✅ employees in selected dept (for modal)
  const employeesInSelectedDept = useMemo(() => {
    if (!selectedDeptId) return [];
    return (Array.isArray(staff) ? staff : []).filter(
      (u) => String(u.department_id) === String(selectedDeptId)
    );
  }, [staff, selectedDeptId]);

  // ✅ helper - get card hierarchy (uses edits if present, else dep values)
  function getCardHierarchy(dep) {
    const key = String(dep.id);
    const edit =
      hierarchyEdits[key] || {
        manager_user_id: dep.manager_user_id ? String(dep.manager_user_id) : "",
        supervisor_user_id: dep.supervisor_user_id
          ? String(dep.supervisor_user_id)
          : "",
        lead_user_id: dep.lead_user_id ? String(dep.lead_user_id) : "",
      };

    return {
      manager: nameOrNone(edit.manager_user_id),
      supervisor: nameOrNone(edit.supervisor_user_id),
      lead: nameOrNone(edit.lead_user_id),
    };
  }

  const loadSubduties = useCallback(
    async (deptId) => {
      if (!deptId) {
        setSubduties([]);
        setErrorSub("");
        return;
      }

      try {
        setErrorSub("");
        const res = await authFetch(`/api/departments/${deptId}/subduties`);

        if (!res.ok)
          throw new Error(`Failed to load sub-duties (status ${res.status})`);
        const data = await res.json();
        setSubduties(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("loadSubduties error:", err);
        setErrorSub(err.message || "Failed to load sub-duties");
      }
    },
    [authFetch]
  );

  useEffect(() => {
    if (!showDeptModal) return;
    const d = departments.find((x) => String(x.id) === String(selectedDeptId));
    if (!d) return;

    setEditName(d.name || "");
    setEditDescription(d.description || "");
    setEditColor(d.color || "#2563eb");
    setEditIcon(d.icon || "FcBriefcase");
  }, [showDeptModal, selectedDeptId, departments]);

  // ---------- LOAD DEPARTMENTS + STAFF ----------
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    async function loadAll() {
      try {
        setLoading(true);
        setError("");

        const depRes = await authFetch("/api/departments");
        const depData = await depRes.json();

        if (!depRes.ok) {
          throw new Error(`Failed to load departments (status ${depRes.status})`);
        }

        setDepartments(Array.isArray(depData) ? depData : []);

        const edits = {};
        for (const d of depData || []) {
          edits[String(d.id)] = {
            manager_user_id: d.manager_user_id ? String(d.manager_user_id) : "",
            supervisor_user_id: d.supervisor_user_id
              ? String(d.supervisor_user_id)
              : "",
            lead_user_id: d.lead_user_id ? String(d.lead_user_id) : "",
          };
        }
        setHierarchyEdits(edits);

        if (!selectedDeptId && depData?.length > 0) {
          setSelectedDeptId(String(depData[0].id));
        }

        try {
          const staffRes = await authFetch("/api/staff");
          const staffData = await staffRes.json();

          if (!staffRes.ok) {
            setStaff([]);
          } else {
            setStaff(Array.isArray(staffData) ? staffData : []);
          }
        } catch (e) {
          setStaff([]);
        }
      } catch (err) {
        console.error("loadAll error:", err);
        setError(err.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    }

    loadAll();
  }, [authFetch]);

  // ---------- SAVE HIERARCHY ----------
  async function doSaveHierarchy(deptId) {
    const key = String(deptId);
    console.log("Saving deptId:", deptId, "selectedDeptId:", selectedDeptId);
    const vals = hierarchyEdits[key] || {
      manager_user_id: "",
      supervisor_user_id: "",
      lead_user_id: "",
    };

    try {
      setSavingDeptId(key);

      const patchRes = await authFetch(`/api/departments/${deptId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          departmentId: Number(deptId),
          name: editName.trim(),
          description: editDescription.trim(),
          color: editColor,
          icon: editIcon,
        }),
      });

      let patchData = null;
      try {
        patchData = await patchRes.json();
      } catch (e) {}

      if (!patchRes.ok) {
        throw new Error(
          patchData?.error ||
            `Failed to save department color/icon (status ${patchRes.status})`
        );
      }

      const res = await authFetch(`/api/departments/${deptId}/hierarchy`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          departmentId: Number(deptId),
          manager_user_id: vals.manager_user_id || null,
          supervisor_user_id: vals.supervisor_user_id || null,
          lead_user_id: vals.lead_user_id || null,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to save hierarchy");

      const depRes = await authFetch("/api/departments");
      const depData = await depRes.json();
      setDepartments(depData);

      setSaveSuccess({
        open: true,
        message: "✅ Department updated successfully",
        isError: false,
      });
    } catch (err) {
      console.error("saveHierarchy error:", err);
      setSaveSuccess({
        open: true,
        message: err.message || "Failed to save",
        isError: true,
      });
    } finally {
      setSavingDeptId(null);
    }
  }

  // ---------- DELETE DEPARTMENT ----------
  async function doDeleteDepartment(deptId) {
    const key = String(deptId);
    try {
      setDeletingDeptId(key);

      const res = await authFetch(`/api/departments/${deptId}`, {
        method: "DELETE",
      });

      let data = null;
      try {
        data = await res.json();
      } catch (e) {}

      if (!res.ok) throw new Error(data?.error || "Failed to delete department");

      const depRes = await authFetch("/api/departments");
      const depData = await depRes.json();
      setDepartments(Array.isArray(depData) ? depData : []);

      try {
        const staffRes = await authFetch("/api/staff");
        const staffData = await staffRes.json();
        setStaff(staffRes.ok ? (Array.isArray(staffData) ? staffData : []) : []);
      } catch (e) {
        setStaff([]);
      }

      const edits = {};
      for (const d of depData || []) {
        edits[String(d.id)] = {
          manager_user_id: d.manager_user_id ? String(d.manager_user_id) : "",
          supervisor_user_id: d.supervisor_user_id
            ? String(d.supervisor_user_id)
            : "",
          lead_user_id: d.lead_user_id ? String(d.lead_user_id) : "",
        };
      }
      setHierarchyEdits(edits);

      if (String(selectedDeptId) === String(deptId)) {
        const next = depData?.[0]?.id ? String(depData[0].id) : "";
        setSelectedDeptId(next);
      }

      setSaveSuccess({
        open: true,
        message: "🗑️ Department deleted successfully",
        isError: false,
      });
    } catch (err) {
      console.error("deleteDepartment error:", err);
      setSaveSuccess({
        open: true,
        message: err.message || "Failed to delete",
        isError: true,
      });
    } finally {
      setDeletingDeptId(null);
    }
  }

  // ---------- ADD NEW DEPARTMENT (NO SUB-DUTIES HERE) ----------
  async function handleCreateDepartmentFromModal(e) {
    e.preventDefault();
    if (!newName.trim()) return;

    try {
      const res = await authFetch("/api/departments", {
        method: "POST",
        body: JSON.stringify({
          name: newName.trim(),
          description: newDescription.trim(),
          color: newColor,
          icon: newIcon,
          manager_user_id: role === "manager" ? user?.id : null,
        }),
      });

      const created = await res.json();
      if (!res.ok) throw new Error(created?.error || "Failed to add department");

      const newDeptId = created.id;

      const depRes = await authFetch("/api/departments");
      const depData = await depRes.json();
      setDepartments(depData);

      setHierarchyEdits((prev) => ({
        ...prev,
        [String(newDeptId)]: {
          manager_user_id: created?.manager_user_id
            ? String(created.manager_user_id)
            : role === "manager"
            ? String(user?.id)
            : "",
          supervisor_user_id: created?.supervisor_user_id
            ? String(created.supervisor_user_id)
            : "",
          lead_user_id: created?.lead_user_id ? String(created.lead_user_id) : "",
        },
      }));

      setSelectedDeptId(String(newDeptId));
      closeModal();
    } catch (err) {
      console.error("handleCreateDepartmentFromModal error:", err);
      setSaveSuccess({
        open: true,
        message: err.message || "Error creating department",
        isError: true,
      });
    }
  }

  // ---------- ADD NEW SUB-DUTY ----------
  async function handleAddSubDuty() {
    if (!selectedDeptId || !newSubName.trim()) return;

    try {
      const res = await authFetch(`/api/departments/${selectedDeptId}/subduties`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newSubName.trim() }),
      });

      const created = await res.json();
      if (!res.ok) throw new Error(created?.error || "Failed to add sub-duty");

      setSubduties((prev) => [...prev, created]);
      setNewSubName("");
    } catch (err) {
      console.error("handleAddSubDuty error:", err);
      setSaveSuccess({
        open: true,
        message: err.message || "Error creating sub-duty",
        isError: true,
      });
    }
  }

  // ✅ NEW: UPDATE SUB-DUTY
  async function handleUpdateSubDuty(subId) {
    if (!selectedDeptId || !subId || !editingSubName.trim()) return;

    try {
      setSavingSubId(String(subId));

      const res = await authFetch(
        `/api/departments/${selectedDeptId}/subduties/${subId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: editingSubName.trim() }),
        }
      );

      let data = null;
      try {
        data = await res.json();
      } catch (e) {}

      if (!res.ok) {
        throw new Error(data?.error || "Failed to update sub-duty");
      }

      setSubduties((prev) =>
        prev.map((sd) =>
          String(sd.id) === String(subId)
            ? { ...sd, name: editingSubName.trim() }
            : sd
        )
      );

      setEditingSubId(null);
      setEditingSubName("");

      setSaveSuccess({
        open: true,
        message: "✅ Sub-duty updated successfully",
        isError: false,
      });
    } catch (err) {
      console.error("handleUpdateSubDuty error:", err);
      setSaveSuccess({
        open: true,
        message: err.message || "Failed to update sub-duty",
        isError: true,
      });
    } finally {
      setSavingSubId(null);
    }
  }

  // ✅ NEW: DELETE SUB-DUTY
  async function handleDeleteSubDuty(subId) {
    if (!selectedDeptId || !subId) return;

    try {
      setDeletingSubDutyId(String(subId));

      const res = await authFetch(
        `/api/departments/${selectedDeptId}/subduties/${subId}`,
        {
          method: "DELETE",
        }
      );

      let data = null;
      try {
        data = await res.json();
      } catch (e) {}

      if (!res.ok) {
        throw new Error(data?.error || "Failed to delete sub-duty");
      }

      setSubduties((prev) =>
        prev.filter((sd) => String(sd.id) !== String(subId))
      );

      if (String(editingSubId) === String(subId)) {
        setEditingSubId(null);
        setEditingSubName("");
      }

      setSaveSuccess({
        open: true,
        message: "🗑️ Sub-duty deleted successfully",
        isError: false,
      });
    } catch (err) {
      console.error("handleDeleteSubDuty error:", err);
      setSaveSuccess({
        open: true,
        message: err.message || "Failed to delete sub-duty",
        isError: true,
      });
    } finally {
      setDeletingSubDutyId(null);
    }
  }

  function closeModal() {
    setShowAddDept(false);
    setNewName("");
    setNewDescription("");
    setNewColor("#2563eb");
    setNewIcon("FcBriefcase");
    setShowIconPicker(false);
  }

  function closeSubModal() {
    setShowSubModal(false);
    setNewSubName("");
    setErrorSub("");
    setEditingSubId(null);
    setEditingSubName("");
    setSavingSubId(null);
    setDeletingSubDutyId(null);
  }

  function closeDeptModal() {
    setShowDeptModal(false);
    setEditName("");
    setEditDescription("");
    setEditColor("#2563eb");
    setEditIcon("FcBriefcase");
    setShowEditIconPicker(false);
  }

  function clearDeptFilters() {
    setDeptSearch("");
    setManagerFilter("all");
    setDeptSort("az");
  }

  const hasActiveDeptFilters =
    deptSearch.trim() !== "" ||
    managerFilter !== "all" ||
    deptSort !== "az";

  async function openDeptModal(deptId) {
    if (deptId) setSelectedDeptId(String(deptId));
    setShowDeptModal(true);
  }

  async function handleMoveEmployee(userId, nextDepartmentId) {
    try {
      setMovingUserId(String(userId));

      const res = await authFetch(`/api/staff/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          department_id: nextDepartmentId ? Number(nextDepartmentId) : null,
        }),
      });

      let data = null;
      try {
        data = await res.json();
      } catch (e) {}

      if (!res.ok) {
        throw new Error(data?.error || "Failed to update employee department");
      }

      try {
        const staffRes = await authFetch("/api/staff");
        const staffData = await staffRes.json();
        setStaff(staffRes.ok ? (Array.isArray(staffData) ? staffData : []) : []);
      } catch (e) {
        setStaff([]);
      }

      setSaveSuccess({
        open: true,
        message: "✅ Employee department updated successfully",
        isError: false,
      });
    } catch (err) {
      console.error("handleMoveEmployee error:", err);
      setSaveSuccess({
        open: true,
        message: err.message || "Failed to update employee department",
        isError: true,
      });
    } finally {
      setMovingUserId(null);
    }
  }

  if (loading) return <p style={{ padding: 20 }}>Loading departments...</p>;
  if (error) return <p style={{ padding: 20, color: "red" }}>{error}</p>;

  const desktopCols = filteredDepartments.length <= 4 ? 2 : 3;

  return (
    <div className="adm-dept-page">
      <style>{`
        .adm-dept-page{
          padding: 22px 24px 32px;
          background: linear-gradient(180deg,#f8fafc 0%,#f1f5f9 100%);
          min-height: calc(100vh - 60px);
          color:#0f172a;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .adm-header{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:16px;
          margin-bottom:16px;
          flex-wrap: wrap;
        }
        .adm-header h1{ margin:0; font-size:28px; letter-spacing:-0.02em; }
        .adm-header p{ margin:6px 0 0; color:#64748b; }

        .adm-header-actions{
          display:flex;
          gap:10px;
        }

        .adm-primary-btn{
          border:none;
          border-radius: 999px;
          padding:10px 14px;
          font-weight:800;
          cursor:pointer;
          color:#0b1120;
          background: linear-gradient(135deg, #38bdf8, #6366f1);
          box-shadow: 0 14px 28px rgba(15,23,42,0.14);
          transition: transform .16s ease, filter .16s ease;
        }
        .adm-primary-btn:hover{ filter: brightness(1.03); transform: translateY(-1px); }
        .adm-primary-btn:active{ transform: translateY(0px); }

        .adm-secondary-btn{
          border:none;
          border-radius: 999px;
          padding:10px 14px;
          font-weight:800;
          cursor:pointer;
          color:#0b1120;
          background: linear-gradient(135deg, #f59e0b, #fb7185);
          box-shadow: 0 14px 28px rgba(15,23,42,0.12);
          transition: transform .16s ease, filter .16s ease;
        }
        .adm-secondary-btn:hover{ filter: brightness(1.03); transform: translateY(-1px); }
        .adm-secondary-btn:active{ transform: translateY(0px); }

        .adm-panel{
          background:#fff;
          border-radius: 22px;
          padding: 16px 16px 18px;
          box-shadow: 0 18px 45px rgba(15,23,42,0.08);
        }

        .adm-panel-title{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:10px;
          margin-bottom:12px;
        }

        .dept-filter-bar{
          display:flex;
          flex-wrap:wrap;
          gap:12px;
          align-items:end;
          margin-bottom:14px;
        }

        .dept-filter-item{
          display:flex;
          flex-direction:column;
          gap:6px;
          min-width:180px;
        }

        .dept-filter-item label{
          font-size:11px;
          color:#64748b;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.04em;
        }

        .dept-filter-item input,
        .dept-filter-item select{
          height:44px;
          border:1px solid #e2e8f0;
          border-radius:14px;
          padding:0 14px;
          background: linear-gradient(180deg, #ffffff, #f8fafc);
          outline:none;
          font-size:13px;
          color:#0f172a;
          box-shadow: 0 1px 2px rgba(15,23,42,0.04);
          transition: border-color .14s ease, box-shadow .14s ease, background .14s ease, transform .14s ease;
        }

        .dept-filter-item input:hover,
        .dept-filter-item select:hover{
          background:#fff;
          border-color:#cbd5e1;
        }

        .dept-filter-item input:focus,
        .dept-filter-item select:focus{
          border-color:#93c5fd;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.14);
          background:#fff;
        }

        .dept-clear-btn{
          height:44px;
          border:1px solid #e2e8f0;
          border-radius:14px;
          padding:0 16px;
          background: linear-gradient(180deg, #ffffff, #f8fafc);
          color:#0f172a;
          font-size:13px;
          font-weight:900;
          cursor:pointer;
          box-shadow: 0 1px 2px rgba(15,23,42,0.04);
          transition: transform .14s ease, filter .14s ease, background .14s ease, border-color .14s ease;
        }

        .dept-clear-btn:hover{
          transform: translateY(-1px);
          filter: brightness(1.01);
          background:#fff;
          border-color:#cbd5e1;
        }

        .adm-panel-title h2{ margin:0; font-size:18px; }
        .adm-panel-title p{ margin:2px 0 0; color:#94a3b8; font-size:13px; }

        .dept-grid{
          margin-top: 12px;
          display:grid;
          gap: 18px;
          align-items: stretch;
        }

        .dept-card{
          position:relative;
          border-radius: 22px;
          border: 1px solid #e7edf6;
          border-left: 4px solid transparent;
          overflow: visible;
          min-height: 200px;
          background: #ffffff;
          transition: all .22s ease;
          box-shadow: 0 8px 20px rgba(15,23,42,0.08);
        }

        .dept-card.tint-0{ background: #ffffff; }
        .dept-card.tint-1{ background: #ffffff; }
        .dept-card.tint-2{ background: #ffffff; }

        .dept-card:hover{
          transform: translateY(-4px);
          border-color: #dbe3ef;
          box-shadow:
            0 20px 40px rgba(15,23,42,0.12),
            0 0 0 1px rgba(255,255,255,0.6) inset;
        }

        .dept-head{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          padding: 12px 12px;
          font-weight: 900;
          font-size: 14px;
          color:#0f172a;
          border-bottom: 1px solid rgba(15,23,42,0.06);
          background: rgba(255,255,255,0.65);
          backdrop-filter: blur(6px);
          border-top-left-radius: 22px;
          border-top-right-radius: 22px;
        }

        .dept-title{
          display:flex;
          align-items:center;
          gap:10px;
        }

        .dept-dot{
          width:8px;
          height:8px;
          border-radius:50%;
          background:#64748b;
        }

        .dept-name{
          font-weight:900;
          color:#0f172a;
        }

        .head-actions{
          display:flex;
          align-items:center;
          gap:8px;
          flex: 0 0 auto;
        }

        .icon-btn{
          width: 34px;
          height: 34px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          background: rgba(255,255,255,0.9);
          display:flex;
          align-items:center;
          justify-content:center;
          cursor:pointer;
          transition: transform .14s ease, filter .14s ease, background .14s ease;
        }

        .icon-btn:hover{
          transform: translateY(-1px);
          filter: brightness(1.02);
          background: #fff;
        }

        .dept-body{
          padding: 10px;
          background: transparent;
        }

        .hier-preview{
          display:grid;
          grid-template-columns: 1fr;
          gap: 8px;
          margin-bottom: 10px;
        }

        .hier-pill{
          border: 1px solid rgba(15,23,42,0.06);
          border-radius: 14px;
          padding: 10px 12px;
          background: rgba(255,255,255,0.78);
        }

        .hier-label{
          font-size: 12px;
          font-weight: 900;
          color: #334155;
          margin-bottom: 2px;
        }

        .hier-name{
          font-size: 12.5px;
          font-weight: 900;
          color: #0f172a;
        }

        .team-row{
          display:flex;
          align-items:center;
          gap:10px;
          padding: 9px 10px;
          border-radius: 14px;
          background: rgba(255,255,255,0.78);
          border: 1px solid rgba(15,23,42,0.06);
        }

        .team-row + .team-row{ margin-top: 8px; }

        .avatar{
          width: 28px;
          height: 28px;
          border-radius: 999px;
          display:flex;
          align-items:center;
          justify-content:center;
          font-size: 12px;
          font-weight: 900;
          color: #0b1120;
          background: #e0f2fe;
          border: 1px solid #bae6fd;
          flex: 0 0 auto;
        }

        .team-name{
          font-weight: 800;
          font-size: 13px;
          color: #0f172a;
          line-height: 1.1;
        }

        .team-meta{
          font-size: 12px;
          color: #64748b;
          margin-top: 2px;
        }

        .empty{
          background:#f8fafc;
          border:1px dashed #cbd5e1;
          border-radius:12px;
          padding:12px;
          text-align:center;
          color:#64748b;
          font-size:13px;
          font-weight:600;
        }

        .dept-footer{
          margin-top:8px;
          font-size:11px;
          color:#64748b;
          font-weight:700;
          text-align:right;
          opacity:.8;
        }

        .btn-ghost{
          border:1px solid rgba(37,99,235,0.18);
          background: linear-gradient(135deg, #eef2ff, #e0f2fe);
          border-radius: 999px;
          padding: 10px 14px;
          font-weight:900;
          cursor:pointer;
          color:#0f172a;
          transition: filter .14s ease, transform .14s ease;
        }

        .btn-ghost:hover{
          filter: brightness(1.02);
          transform: translateY(-1px);
        }

        .btn-save{
          border:none;
          border-radius: 999px;
          padding: 10px 16px;
          font-weight:900;
          cursor:pointer;
          background:#0b1120;
          color:#fff;
        }

        .btn-save:disabled{ opacity:.6; cursor:not-allowed; }

        .field label{
          display:block;
          font-size:12px;
          color:#334155;
          font-weight:800;
          margin-bottom:6px;
        }

        .field select, .field input{
          width:100%;
          border:1px solid #e5e7eb;
          border-radius: 12px;
          padding: 10px 12px;
          background:#fff;
          outline:none;
        }

        .field select:focus, .field input:focus{
          border-color:#93c5fd;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
        }

        .modal-overlay{
          position:fixed; inset:0;
          background: rgba(2,6,23,0.45);
          display:flex;
          align-items:center;
          justify-content:center;
          padding: 24px;
          z-index: 50;
        }

        .modal{
          width: min(820px, 94vw);
          max-height: 90vh;
          background:#fff;
          border-radius: 18px;
          box-shadow: 0 28px 70px rgba(15,23,42,0.35);
          overflow:hidden;
          display:flex;
          flex-direction:column;
        }

        .modal-header{
          display:flex;
          align-items:center;
          justify-content:space-between;
          padding: 14px 16px;
          border-bottom: 1px solid #eef2f7;
        }

        .modal-header h4{
          margin:0;
          font-size:16px;
          font-weight:900;
        }

        .x-btn{
          border:none;
          background:transparent;
          font-size:20px;
          cursor:pointer;
          color:#64748b;
        }

        .modal-footer{
          padding: 14px 16px;
          border-top: 1px solid #eef2f7;
          display:flex;
          justify-content:flex-end;
          gap:10px;
        }

        .modal-body-two{
          padding: 16px;
          display:grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
          align-items: start;
          overflow-y: auto;
          flex: 1;
        }

        .modal-card{
          border:1px solid #eef2f7;
          border-radius: 16px;
          padding: 14px;
          background:#fff;
          align-self: start;
          min-height: 100%;
        }

        .modal-card h5{
          margin:0 0 10px;
          font-size:14px;
          font-weight:900;
        }

        .sub-btn{
          border:1px solid #bfdbfe;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border-radius: 999px;
          padding:9px 14px;
          font-weight:900;
          cursor:pointer;
          color:#1d4ed8;
          transition: all .16s ease;
          box-shadow: 0 6px 16px rgba(59,130,246,0.10);
        }
        .sub-btn:hover{
          background: linear-gradient(135deg, #dbeafe, #bfdbfe);
          border-color:#93c5fd;
          transform: translateY(-1px);
        }
        .sub-btn:disabled{
          opacity:.5;
          cursor:not-allowed;
          transform:none;
        }

        .sub-box{
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 12px;
          background: #fff;
          overflow: hidden;
        }

        .sub-list{
          margin:10px 0 0;
          padding:0;
          list-style:none;
          display:flex;
          flex-direction:column;
          gap:8px;
        }

        .sub-item{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          padding: 12px 14px;
          border-radius: 16px;
          background: linear-gradient(180deg, #f8fbff, #f1f7ff);
          border:1px solid #dbeafe;
          font-weight:700;
          color:#0f172a;
          flex-wrap: wrap;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.75);
        }

        .mini-modal{
          width: min(520px, 92vw);
          background:#fff;
          border-radius: 18px;
          box-shadow: 0 28px 70px rgba(15,23,42,0.35);
          overflow:hidden;
        }

        .mini-head{
          padding: 14px 16px;
          border-bottom: 1px solid #eef2f7;
          display:flex;
          align-items:center;
          justify-content:space-between;
        }

        .mini-head h4{
          margin:0;
          font-size:16px;
          font-weight:900;
        }

        .mini-body{
          padding: 14px 16px;
          color:#334155;
          font-size:14px;
          line-height:1.4;
        }

        .mini-actions{
          padding: 14px 16px;
          border-top: 1px solid #eef2f7;
          display:flex;
          justify-content:flex-end;
          gap:10px;
        }

        .btn-cancel{
          border:1px solid #cbd5e1;
          background: linear-gradient(180deg, #ffffff, #f8fafc);
          border-radius: 999px;
          padding: 9px 14px;
          font-weight:900;
          cursor:pointer;
          color:#334155;
          transition: all .16s ease;
        }
        .btn-cancel:hover{
          background:#fff;
          border-color:#94a3b8;
          transform: translateY(-1px);
        }

        .btn-confirm{
          border:none;
          border-radius: 999px;
          padding: 10px 14px;
          font-weight:900;
          cursor:pointer;
          background:#2563eb;
          color:#fff;
        }

        .btn-confirm:disabled{ opacity:.7; cursor:not-allowed; }

        .btn-danger{
          border:1px solid #bfdbfe;
          background: linear-gradient(135deg, #dbeafe, #93c5fd);
          border-radius: 999px;
          padding: 9px 14px;
          font-weight:900;
          cursor:pointer;
          color:#0f172a;
          transition: all .16s ease;
          box-shadow: 0 6px 16px rgba(59,130,246,0.12);
        }
        .btn-danger:hover{
          background: linear-gradient(135deg, #bfdbfe, #60a5fa);
          border-color:#60a5fa;
          transform: translateY(-1px);
        }
        .btn-danger:disabled{
          opacity:.65;
          cursor:not-allowed;
          transform:none;
        }

        @media (max-width: 1100px){
          .modal-body-two{ grid-template-columns: 1fr; }
        }

        @media (max-width: 740px){
          .adm-dept-page{
            padding: 14px 12px 24px;
            overflow-x: hidden;
          }

          .adm-header{
            gap: 12px;
          }

          .adm-header-actions{
            display:flex;
            gap:10px;
            flex-wrap:wrap;
            width:100%;
          }

          .adm-primary-btn,
          .adm-secondary-btn{
            flex: 1 1 140px;
            min-width: 0;
            text-align: center;
            padding: 10px 12px;
          }

          .adm-panel{
            padding: 14px 12px 16px;
          }

          .adm-panel-title{
            flex-wrap: wrap;
            align-items: flex-start;
          }

          .btn-ghost{
            align-self: flex-start;
          }
        }

        @media (max-width: 740px){
          .dept-grid{ grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Header */}
      <div className="adm-header">
        <div>
          <h1>Departments</h1>
        </div>

        {canManage && (
          <div className="adm-header-actions">
            <button
              className="adm-primary-btn"
              type="button"
              onClick={() => setShowAddDept(true)}
            >
              + Add Department
            </button>

            <button
              className="adm-secondary-btn"
              type="button"
              onClick={() => {
                setShowSubModal(true);
                if (selectedDeptId) loadSubduties(selectedDeptId);
              }}
            >
              Sub-duties
            </button>
          </div>
        )}
      </div>

      {/* ✅ MAIN */}
      <div className="adm-panel">
        <div className="adm-panel-title">
          <div>
            <h2>All Departments</h2>
            <p>
              Use the edit icon on a department card to manage hierarchy
              (Manager / Supervisor / Lead).
            </p>
          </div>

          <button
            className="btn-ghost"
            type="button"
            onClick={() => openDeptModal(selectedDeptId || departments?.[0]?.id)}
          >
            All Departments
          </button>
        </div>

        <div className="dept-filter-bar">
          <div className="dept-filter-item" style={{ flex: "1 1 280px" }}>
            <label>Search Department</label>
            <input
              type="text"
              placeholder="Search department name or description..."
              value={deptSearch}
              onChange={(e) => setDeptSearch(e.target.value)}
            />
          </div>

          <div className="dept-filter-item">
            <label>Manager</label>
            <select
              value={managerFilter}
              onChange={(e) => setManagerFilter(e.target.value)}
            >
              <option value="all">All Managers</option>
              {managerOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="dept-filter-item">
            <label>Sort</label>
            <select
              value={deptSort}
              onChange={(e) => setDeptSort(e.target.value)}
            >
              <option value="az">A–Z</option>
              <option value="most">Most employees</option>
              <option value="least">Least employees</option>
            </select>
          </div>

          {hasActiveDeptFilters && (
            <div className="dept-filter-item" style={{ minWidth: "150px" }}>
              <label>&nbsp;</label>
              <button
                type="button"
                className="dept-clear-btn"
                onClick={clearDeptFilters}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        <div
          className="dept-grid"
          style={{
            gridTemplateColumns: `repeat(${desktopCols}, minmax(0, 1fr))`,
          }}
        >
          {filteredDepartments.length === 0 && (
            <div className="empty" style={{ gridColumn: "1 / -1" }}>
              No departments found for this filter.
            </div>
          )}

          {filteredDepartments.map((dep, idx) => {
            const did = String(dep.id);
            const deptStaff = staffByDept[did] || [];
            const hier = getCardHierarchy(dep);
            const tintClass = `tint-${idx % 3}`;

            return (
              <div
                className={`dept-card ${tintClass}`}
                key={dep.id}
                style={{ borderLeftColor: dep.color || "#e5e7eb" }}
              >
                <div
                  className="dept-head"
                  style={{
                    borderBottom: `3px solid ${dep.color || "#e5e7eb"}`,
                  }}
                >
                  <div className="dept-title">
                    <span
                      className="dept-dot"
                      style={{ background: dep.color || "#64748b" }}
                    ></span>

                    {dep.icon && departmentIconMap[dep.icon]
                      ? React.createElement(departmentIconMap[dep.icon], { size: 18 })
                      : React.createElement(departmentIconMap.FcBriefcase, { size: 18 })}

                    <span className="dept-name">{dep.name}</span>
                  </div>

                  {canManage && (
                    <div className="head-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit hierarchy"
                        aria-label={`Edit ${dep.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeptModal(dep.id);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path
                            d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0 0-3L16.5 4.5a2.1 2.1 0 0 0-3 0L3 15v5z"
                            stroke="#0f172a"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M13.5 6.5l4 4"
                            stroke="#0f172a"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      <button
                        type="button"
                        className="icon-btn"
                        title="Delete department"
                        aria-label={`Delete ${dep.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete({
                            open: true,
                            deptId: dep.id,
                            deptName: dep.name,
                          });
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M3 6h18" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M8 6V4h8v2" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M6 6l1 16h10l1-16" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10 11v6" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
                          <path d="M14 11v6" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                <div className="dept-body">
                  <div className="hier-preview">
                    <div className="hier-pill">
                      <div className="hier-label">Manager</div>
                      <div className="hier-name">{hier.manager}</div>
                    </div>
                    <div className="hier-pill">
                      <div className="hier-label">Operations Supervisor</div>
                      <div className="hier-name">{hier.supervisor}</div>
                    </div>
                    <div className="hier-pill">
                      <div className="hier-label">Lead</div>
                      <div className="hier-name">{hier.lead}</div>
                    </div>
                  </div>

                  {deptStaff.length === 0 ? (
                    <div className="empty">No employees assigned.</div>
                  ) : (
                    deptStaff.map((u) => (
                      <div className="team-row" key={u.id}>
                        <div className="avatar">{initials(u.name)}</div>
                        <div>
                          <div className="team-name">{u.name}</div>
                          <div className="team-meta">
                            {u.role || "user"} • ID {u.id}
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  <div className="dept-footer">
                    {deptStaff.length} employee{deptStaff.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ✅ MODAL: All Departments Form */}
      {showDeptModal && (
        <div className="modal-overlay" onMouseDown={closeDeptModal}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>All Departments (Hierarchy)</h4>
              <button className="x-btn" onClick={closeDeptModal} aria-label="Close">
                ×
              </button>
            </div>

            <div className="modal-body-two">
              <div className="modal-card">
                <h5>Department</h5>

                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Select department</label>
                  <select
                    value={selectedDeptId}
                    onChange={(e) => setSelectedDeptId(e.target.value)}
                    disabled={!canManage}
                  >
                    {departments.map((d) => (
                      <option key={d.id} value={String(d.id)}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Department Name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Enter department name"
                    disabled={!canManageDepartments}
                  />
                </div>

                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Description</label>
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Enter department description"
                    disabled={!canManageDepartments}
                  />
                </div>

                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Department Color</label>
                  <input
                    type="color"
                    value={editColor}
                    onChange={(e) => setEditColor(e.target.value)}
                    style={{ height: 44, padding: 6 }}
                    disabled={!canManageDepartments}
                  />
                </div>

                <div className="field" style={{ marginBottom: 12 }}>
                  <label>Department Icon</label>

                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setShowEditIconPicker(true)}
                    disabled={!canManageDepartments}
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                      {departmentIconMap[editIcon]
                        ? React.createElement(departmentIconMap[editIcon], { size: 18 })
                        : React.createElement(departmentIconMap.FcBriefcase, { size: 18 })}
                    </span>
                    Choose Icon
                  </button>
                </div>

                <div className="field" style={{ marginBottom: 10 }}>
                  <label>Manager</label>
                  <select
                    value={selectedEdit.manager_user_id}
                    onChange={(e) =>
                      setHierarchyEdits((prev) => ({
                        ...prev,
                        [selectedKey]: { ...selectedEdit, manager_user_id: e.target.value },
                      }))
                    }
                    disabled={!canManageDepartments}
                  >
                    {staffOptions.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field" style={{ marginBottom: 10 }}>
                  <label>Operations Supervisor</label>
                  <select
                    value={selectedEdit.supervisor_user_id}
                    onChange={(e) =>
                      setHierarchyEdits((prev) => ({
                        ...prev,
                        [selectedKey]: { ...selectedEdit, supervisor_user_id: e.target.value },
                      }))
                    }
                    disabled={!canManageDepartments}
                  >
                    {staffOptions.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Lead</label>
                  <select
                    value={selectedEdit.lead_user_id}
                    onChange={(e) =>
                      setHierarchyEdits((prev) => ({
                        ...prev,
                        [selectedKey]: { ...selectedEdit, lead_user_id: e.target.value },
                      }))
                    }
                    disabled={!isSuperadmin}
                  >
                    {staffOptions.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginTop: 12, fontSize: 12, color: "#64748b" }}>
                  Current: <b>{selectedDeptName}</b> <br />
                  Manager: <b>{nameOrNone(selectedEdit.manager_user_id)}</b> • Supervisor:{" "}
                  <b>{nameOrNone(selectedEdit.supervisor_user_id)}</b> • Lead:{" "}
                  <b>{nameOrNone(selectedEdit.lead_user_id)}</b>
                </div>
              </div>

              <div className="modal-card">
                <h5>Employees in {selectedDeptName}</h5>

                {employeesInSelectedDept.length === 0 ? (
                  <div className="empty">No employees assigned to this department yet.</div>
                ) : (
                  employeesInSelectedDept.map((u) => (
                    <div
                      className="team-row"
                      key={u.id}
                      style={{ alignItems: "stretch", flexDirection: "column" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          width: "100%",
                        }}
                      >
                        <div className="avatar">{initials(u.name)}</div>
                        <div style={{ flex: 1 }}>
                          <div className="team-name">{u.name}</div>
                          <div className="team-meta">{u.role || "user"} • ID {u.id}</div>
                        </div>
                      </div>

                      {canManageDepartments && (
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                            marginTop: 10,
                            width: "100%",
                          }}
                        >
                          <select
                            value={u.department_id ? String(u.department_id) : ""}
                            onChange={(e) => handleMoveEmployee(u.id, e.target.value)}
                            disabled={movingUserId === String(u.id)}
                            style={{
                              flex: "1 1 220px",
                              minWidth: 180,
                              border: "1px solid #e5e7eb",
                              borderRadius: 12,
                              padding: "10px 12px",
                              background: "#fff",
                              outline: "none",
                            }}
                          >
                            <option value="">No Department</option>
                            {departments.map((d) => (
                              <option key={d.id} value={String(d.id)}>
                                {d.name}
                              </option>
                            ))}
                          </select>

                          <button
                            type="button"
                            className="btn-cancel"
                            disabled={movingUserId === String(u.id)}
                            onClick={() => handleMoveEmployee(u.id, "")}
                          >
                            {movingUserId === String(u.id) ? "Updating..." : "Remove"}
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}

                <div style={{ marginTop: 10, color: "#64748b", fontSize: 12 }}>
                  Tip: Assign staff to departments from the <b>Staff</b> page.
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-ghost" type="button" onClick={closeDeptModal}>
                Close
              </button>

              {canManageDepartments && (
                <button
                  className="btn-save"
                  type="button"
                  disabled={!selectedDeptId || savingDeptId === String(selectedDeptId)}
                  onClick={() => {
                    setConfirmSave({
                      open: true,
                      deptId: selectedDeptId,
                      deptName: selectedDeptName,
                    });
                  }}
                >
                  {savingDeptId === String(selectedDeptId) ? "Saving..." : "Save"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ✅ Confirm Save Modal */}
      {confirmSave.open && (
        <div
          className="modal-overlay"
          onMouseDown={() => setConfirmSave({ open: false, deptId: null, deptName: "" })}
        >
          <div className="mini-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mini-head">
              <h4>Confirm Save</h4>
              <button
                className="x-btn"
                onClick={() => setConfirmSave({ open: false, deptId: null, deptName: "" })}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="mini-body">
              Save hierarchy changes for <b>{confirmSave.deptName || "this department"}</b>?
            </div>
            <div className="mini-actions">
              <button
                className="btn-cancel"
                type="button"
                onClick={() => setConfirmSave({ open: false, deptId: null, deptName: "" })}
              >
                Cancel
              </button>
              <button
                className="btn-confirm"
                type="button"
                disabled={savingDeptId === String(confirmSave.deptId || "")}
                onClick={async () => {
                  const deptId = confirmSave.deptId;
                  setConfirmSave({ open: false, deptId: null, deptName: "" });
                  if (!deptId) return;
                  await doSaveHierarchy(deptId);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Confirm Delete Modal */}
      {confirmDelete.open && (
        <div
          className="modal-overlay"
          onMouseDown={() => setConfirmDelete({ open: false, deptId: null, deptName: "" })}
        >
          <div className="mini-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mini-head">
              <h4>Delete Department</h4>
              <button
                className="x-btn"
                onClick={() => setConfirmDelete({ open: false, deptId: null, deptName: "" })}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="mini-body">
              Are you sure you want to delete <b>{confirmDelete.deptName || "this department"}</b>?
              <div style={{ marginTop: 8, color: "#64748b", fontSize: 12 }}>
                If this department has staff assigned, backend may block delete.
              </div>
            </div>
            <div className="mini-actions">
              <button
                className="btn-cancel"
                type="button"
                onClick={() => setConfirmDelete({ open: false, deptId: null, deptName: "" })}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                type="button"
                disabled={deletingDeptId === String(confirmDelete.deptId || "")}
                onClick={async () => {
                  const deptId = confirmDelete.deptId;
                  setConfirmDelete({ open: false, deptId: null, deptName: "" });
                  if (!deptId) return;
                  await doDeleteDepartment(deptId);
                }}
              >
                {deletingDeptId === String(confirmDelete.deptId || "") ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Success/Error Modal */}
      {saveSuccess.open && (
        <div
          className="modal-overlay"
          onMouseDown={() => setSaveSuccess({ open: false, message: "", isError: false })}
        >
          <div className="mini-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mini-head">
              <h4>{saveSuccess.isError ? "Error" : "Done"}</h4>
              <button
                className="x-btn"
                onClick={() => setSaveSuccess({ open: false, message: "", isError: false })}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="mini-body">{saveSuccess.message}</div>
            <div className="mini-actions">
              <button
                className="btn-save"
                type="button"
                onClick={() => {
                  setSaveSuccess({ open: false, message: "", isError: false });
                  if (!saveSuccess.isError && showDeptModal) closeDeptModal();
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Sub-duties Modal */}
      {showSubModal && (
        <div className="modal-overlay" onMouseDown={closeSubModal}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Sub-duties</h4>
              <button className="x-btn" onClick={closeSubModal} aria-label="Close">
                ×
              </button>
            </div>

            <div
              style={{
                padding: 16,
                overflowY: "auto",
                flex: 1,
                minHeight: 0,
              }}
            >
              <p style={{ margin: "0 0 10px", color: "#64748b", fontSize: 13 }}>
                For <b>{selectedDeptName}</b>, add sub-duties (Driver seat, Scanner, Cameras…)
              </p>

              <div
                className="sub-box"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <div className="field" style={{ marginBottom: 10 }}>
                  <label>Selected department</label>
                  <select
                    value={selectedDeptId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedDeptId(id);
                      loadSubduties(id);
                    }}
                  >
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>

                {errorSub && <p style={{ color: "red", marginTop: 8 }}>{errorSub}</p>}

                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    maxHeight: "45vh",
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {subduties.length === 0 ? (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: "#f8fafc",
                        color: "#64748b",
                      }}
                    >
                      No sub-duties yet for this department.
                    </div>
                  ) : (
                    <ul className="sub-list">
                      {subduties.map((sd) => {
                        const isEditing = String(editingSubId) === String(sd.id);
                        const isSaving = String(savingSubId) === String(sd.id);
                        const isDeleting = String(deletingSubDutyId) === String(sd.id);

                        return (
                          <li className="sub-item" key={sd.id}>
                            {isEditing ? (
                              <>
                                <input
                                  value={editingSubName}
                                  onChange={(e) => setEditingSubName(e.target.value)}
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    borderRadius: 12,
                                    border: "1px solid #bfdbfe",
                                    padding: "10px 12px",
                                    background: "#ffffff",
                                    boxShadow: "0 0 0 3px rgba(59,130,246,0.08)",
                                    color: "#0f172a",
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleUpdateSubDuty(sd.id);
                                    }
                                    if (e.key === "Escape") {
                                      setEditingSubId(null);
                                      setEditingSubName("");
                                    }
                                  }}
                                  autoFocus
                                />

                                <div
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      flexShrink: 0,
                                      alignItems: "center",
                                    }}
                                  >
                                  <button
                                    type="button"
                                    className="sub-btn"
                                    disabled={!editingSubName.trim() || isSaving}
                                    onClick={() => handleUpdateSubDuty(sd.id)}
                                  >
                                    {isSaving ? "Saving..." : "Save"}
                                  </button>

                                  <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={() => {
                                      setEditingSubId(null);
                                      setEditingSubName("");
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <span style={{ flex: 1, minWidth: 0 }}>{sd.name}</span>

                                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                                  <button
                                    type="button"
                                    className="sub-btn"
                                    onClick={() => {
                                      setEditingSubId(sd.id);
                                      setEditingSubName(sd.name || "");
                                    }}
                                  >
                                    Edit
                                  </button>

                                  <button
                                    type="button"
                                    className="btn-danger"
                                    disabled={isDeleting}
                                    onClick={() => handleDeleteSubDuty(sd.id)}
                                  >
                                    {isDeleting ? "Deleting..." : "Delete"}
                                  </button>
                                </div>
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div
                  style={{
                    marginTop: 12,
                    display: "flex",
                    gap: 8,
                    background: "#fff",
                    paddingTop: 8,
                    flexWrap: "wrap",
                    flexShrink: 0,
                  }}
                >
                  <input
                    className="field"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      borderRadius: 12,
                      border: "1px solid #e5e7eb",
                      padding: "9px 10px",
                    }}
                    placeholder="e.g. Driver seat"
                    value={newSubName}
                    onChange={(e) => setNewSubName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddSubDuty();
                      }
                    }}
                  />
                  <button
                    className="sub-btn"
                    style={{ flexShrink: 0 }}
                    disabled={!newSubName.trim()}
                    onClick={handleAddSubDuty}
                  >
                    + Add Sub-duty
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-ghost" onClick={closeSubModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Add Department Modal */}
      {showAddDept && (
        <div className="modal-overlay" onMouseDown={closeModal}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Add Department</h4>
              <button className="x-btn" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>

            <form onSubmit={handleCreateDepartmentFromModal}>
              <div style={{ padding: 16 }}>
                <div className="modal-card">
                  <h5>Department details</h5>

                  <div className="field" style={{ marginTop: 10 }}>
                    <label>Department Color</label>
                    <input
                      type="color"
                      value={newColor}
                      onChange={(e) => setNewColor(e.target.value)}
                      style={{ height: 44, padding: 6 }}
                    />
                  </div>

                  <div className="field" style={{ marginTop: 10 }}>
                    <label>Department Icon</label>

                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => setShowIconPicker(true)}
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center" }}>
                        {departmentIconMap[newIcon]
                          ? React.createElement(departmentIconMap[newIcon], { size: 18 })
                          : null}
                      </span>
                      Choose Icon
                    </button>
                  </div>

                  <div className="field" style={{ marginBottom: 10 }}>
                    <label>Department name</label>
                    <input
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="e.g. Gatekeeper"
                      required
                    />
                  </div>

                  <div className="field">
                    <label>Description</label>
                    <input
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                      placeholder="e.g. Gatekeeper team"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-ghost" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="adm-primary-btn">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showIconPicker && (
        <div className="modal-overlay" onMouseDown={() => setShowIconPicker(false)}>
          <div className="mini-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mini-head">
              <h4>Select Icon</h4>
              <button className="x-btn" onClick={() => setShowIconPicker(false)}>×</button>
            </div>

            <div style={{ padding: 14 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)",
                  gap: 10,
                }}
              >
                {departmentIconList.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className="icon-btn"
                    style={{ width: "100%", height: 54 }}
                    onClick={() => {
                      setNewIcon(key);
                      setShowIconPicker(false);
                    }}
                    title={key}
                  >
                    {departmentIconMap[key]
                      ? React.createElement(departmentIconMap[key], { size: 22 })
                      : null}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditIconPicker && (
        <div
          className="modal-overlay"
          onMouseDown={() => setShowEditIconPicker(false)}
        >
          <div className="mini-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mini-head">
              <h4>Select Icon</h4>
              <button
                className="x-btn"
                onClick={() => setShowEditIconPicker(false)}
              >
                ×
              </button>
            </div>

            <div style={{ padding: 14 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)",
                  gap: 10,
                }}
              >
                {departmentIconList.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className="icon-btn"
                    style={{ width: "100%", height: 54 }}
                    onClick={() => {
                      setEditIcon(key);
                      setShowEditIconPicker(false);
                    }}
                    title={key}
                  >
                    {departmentIconMap[key]
                      ? React.createElement(departmentIconMap[key], { size: 22 })
                      : null}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDepartmentsPage;